import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { getPostLoginRoute, isCarouselOnlyApp } from '@/lib/app-variant';
import { Eye, EyeOff, Mail, Lock, Loader2, ShoppingBag, Fingerprint, Play } from 'lucide-react';
import { checkAppVersion, getAppStoreUrl, VersionCheckResult } from '@/services/app-version-check';
import { UpdateRequiredModal } from '@/components/ui/update-required-modal';
// Import biometric functions with error handling
let biometricFunctions: {
  isBiometricAvailable: typeof import('@/services/biometric').isBiometricAvailable;
  authenticateWithBiometrics: typeof import('@/services/biometric').authenticateWithBiometrics;
  storeCredentials: typeof import('@/services/biometric').storeCredentials;
  getStoredCredentials: typeof import('@/services/biometric').getStoredCredentials;
  isBiometricEnabled: typeof import('@/services/biometric').isBiometricEnabled;
  hasCredentialsSaved: typeof import('@/services/biometric').hasCredentialsSaved;
} | null = null;

// Lazy load biometric functions to prevent import errors from crashing the app
const loadBiometricFunctions = async () => {
  if (biometricFunctions) return biometricFunctions;
  try {
    const module = await import('@/services/biometric');
    biometricFunctions = {
      isBiometricAvailable: module.isBiometricAvailable,
      authenticateWithBiometrics: module.authenticateWithBiometrics,
      storeCredentials: module.storeCredentials,
      getStoredCredentials: module.getStoredCredentials,
      isBiometricEnabled: module.isBiometricEnabled,
      hasCredentialsSaved: module.hasCredentialsSaved,
    };
    return biometricFunctions;
  } catch (error) {
    console.error('[Login] Failed to load biometric module:', error);
    return null;
  }
};

const Login = () => {
  console.log('[Login] Component rendering...');
  console.log('[Login] Platform check - isNative:', Capacitor.isNativePlatform());
  console.log('[Login] Platform:', Capacitor.getPlatform());
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBiometricChecking, setIsBiometricChecking] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [versionCheckResult, setVersionCheckResult] = useState<VersionCheckResult | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, loading, signIn } = useAuth();
  const postLoginRoute = getPostLoginRoute();
  
  console.log('[Login] Auth state - loading:', loading, 'user:', user ? 'exists' : 'null');

  // Check biometric availability on mount
  useEffect(() => {
    let isMounted = true;
    
    const checkBiometric = async () => {
      try {
        console.log('[Login] useEffect: Check biometric availability - running...');
        console.log('[Login] Checking biometric availability on mount...');
        console.log('[Login] Is native platform:', Capacitor.isNativePlatform());
        
        // Only check biometric on native platforms
        if (!Capacitor.isNativePlatform()) {
          console.log('[Login] Not on native platform, skipping biometric check');
          if (isMounted) setBiometricAvailable(false);
          return;
        }
        
        const biometric = await loadBiometricFunctions();
        if (!biometric) {
          console.log('[Login] Biometric functions not available');
          if (isMounted) setBiometricAvailable(false);
          return;
        }
        
        console.log('[Login] Calling isBiometricAvailable()...');
        const available = await biometric.isBiometricAvailable();
        console.log('[Login] Biometric available result:', available);
        if (isMounted) setBiometricAvailable(available);
      } catch (error) {
        console.error('[Login] Error checking biometric availability:', error);
        console.error('[Login] Error details:', error);
        if (error instanceof Error) {
          console.error('[Login] Error message:', error.message);
          console.error('[Login] Error stack:', error.stack);
        }
        if (isMounted) setBiometricAvailable(false);
      }
    };
    
    // Wrap in try-catch to prevent crashes
    try {
      checkBiometric();
    } catch (error) {
      console.error('[Login] Fatal error in biometric check useEffect:', error);
      setBiometricAvailable(false);
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Check if stored credentials exist (without prompting for biometrics)
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout | null = null;
    
    const checkStoredCredentials = async () => {
      try {
        console.log('[Login] Checking if stored credentials exist...');
        console.log('[Login] Current state - loading:', loading, 'user:', user ? 'exists' : 'null');
        console.log('[Login] Is native platform:', Capacitor.isNativePlatform());
        
        // Only check on native platforms
        if (!Capacitor.isNativePlatform()) {
          console.log('[Login] Not on native platform, skipping credential check');
          if (mounted) setHasStoredCredentials(false);
          return;
        }
        
        // Wait for initial auth check to complete
        if (loading) {
          console.log('[Login] Still loading, skipping credential check');
          return;
        }
        // Don't check if user is already logged in
        if (user) {
          console.log('[Login] User already logged in, skipping credential check');
          if (mounted) setHasStoredCredentials(false);
          return;
        }
        
        const biometric = await loadBiometricFunctions();
        if (!biometric) {
          console.log('[Login] Biometric functions not available, skipping');
          if (mounted) setHasStoredCredentials(false);
          return;
        }
        
        console.log('[Login] Checking if credentials are saved...');
        const hasSaved = await biometric.hasCredentialsSaved();
        console.log('[Login] Has stored credentials:', hasSaved);
        if (mounted) {
          setHasStoredCredentials(hasSaved);
        }
      } catch (error) {
        console.error('[Login] Error checking stored credentials:', error);
        if (error instanceof Error) {
          console.error('[Login] Error message:', error.message);
          console.error('[Login] Error stack:', error.stack);
        }
        if (mounted) {
          setHasStoredCredentials(false);
        }
      }
    };

    // Only run if not loading and no user
    if (!loading && !user) {
      // Small delay to ensure auth state is initialized
      console.log('[Login] Setting timeout to check stored credentials...');
      timeoutId = setTimeout(() => {
        if (mounted) {
          console.log('[Login] Timeout fired, checking stored credentials');
          checkStoredCredentials();
        }
      }, 500);
    }

    return () => {
      console.log('[Login] Cleaning up stored credentials check');
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading, user]);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && user) {
      navigate(postLoginRoute);
    }
  }, [user, loading, navigate, postLoginRoute]);


  const handleBiometricLogin = async () => {
    console.log('[Login] handleBiometricLogin called');
    console.log('[Login] Is native platform:', Capacitor.isNativePlatform());
    
    if (!Capacitor.isNativePlatform()) {
      toast({
        title: "Hata",
        description: "Biyometrik kimlik doğrulama sadece mobil cihazlarda kullanılabilir.",
        variant: "destructive"
      });
      return;
    }
    
    setIsBiometricChecking(true);
    try {
      const biometric = await loadBiometricFunctions();
      if (!biometric) {
        toast({
          title: "Hata",
          description: "Biyometrik özellikler yüklenemedi.",
          variant: "destructive"
        });
        setIsBiometricChecking(false);
        return;
      }
      
      // CRITICAL SECURITY FIX: First explicitly authenticate with Face ID/Touch ID
      // This ensures the user must actually authenticate before we proceed
      console.log('[Login] Requesting Face ID authentication...');
      const authenticated = await biometric.authenticateWithBiometrics('Giriş yapmak için kimliğinizi doğrulayın');
      
      if (!authenticated) {
        console.log('[Login] Face ID authentication failed or was cancelled');
        toast({
          title: "İptal Edildi",
          description: "Kimlik doğrulama başarısız veya iptal edildi.",
          variant: "destructive"
        });
        setIsBiometricChecking(false);
        return;
      }
      
      console.log('[Login] Face ID authentication successful, retrieving stored credentials...');
      // Only retrieve credentials after successful biometric authentication
      const credentials = await biometric.getStoredCredentials();
      if (!credentials) {
        console.log('[Login] No stored credentials found');
        // Update the flag if credentials don't exist
        setHasStoredCredentials(false);
        toast({
          title: "Hata",
          description: "Kayıtlı kimlik bilgileri bulunamadı.",
          variant: "destructive"
        });
        setIsBiometricChecking(false);
        return;
      }

      console.log('[Login] Signing in with stored credentials...');
      setIsLoading(true);
      const { error, user } = await signIn(credentials.email, credentials.password);
      console.log('[Login] Sign in result - error:', error ? 'yes' : 'no', 'user:', user ? 'exists' : 'null');
      
      if (error) {
        toast({
          title: "Giriş Hatası",
          description: error.message || "E-posta veya şifre yanlış. Lütfen tekrar deneyin.",
          variant: "destructive"
        });
        // Clear invalid credentials
        try {
          const biometric = await loadBiometricFunctions();
          if (biometric) {
            const module = await import('@/services/biometric');
            await module.clearStoredCredentials();
            setHasStoredCredentials(false);
          }
        } catch (clearError) {
          console.error('[Login] Error clearing credentials:', clearError);
        }
      } else if (user) {
        toast({
          title: "Başarılı",
          description: "Face ID ile giriş başarılı!",
        });
          
          // Check app version after successful biometric login
          console.log('[Login] Checking app version after biometric login...');
          try {
            const versionResult = await checkAppVersion();
            console.log('[Login] Version check result:', versionResult);
            
            if (versionResult.needsUpdate && versionResult.requirement) {
              console.log('[Login] ⚠️ App version is outdated, showing update modal');
              setVersionCheckResult(versionResult);
              setShowUpdateModal(true);
              // Don't navigate if update is required
              return;
            }
          } catch (versionError) {
            console.error('[Login] Error checking app version:', versionError);
            // Continue with navigation even if version check fails
          }
          
          // Navigate to home if version is OK
          navigate(postLoginRoute);
        }
    } catch (error) {
      console.error('[Login] Biometric login error:', error);
      console.error('[Login] Error details:', JSON.stringify(error, null, 2));
      toast({
        title: "Hata",
        description: "Biyometrik giriş sırasında bir hata oluştu.",
        variant: "destructive"
      });
    } finally {
      setIsBiometricChecking(false);
      setIsLoading(false);
    }
  };

  // Show loading while checking authentication or biometric
  // But don't show loading if we need to show the save credentials dialog
  if (loading || isBiometricChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{isBiometricChecking ? 'Biyometrik kimlik doğrulama yapılıyor...' : 'Yükleniyor...'}</span>
        </div>
      </div>
    );
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDemoLogin = async () => {
    setIsLoading(true);
    setFormData({ email: 'demo@musterix.com', password: '123456' });
    try {
      const { error } = await signIn('demo@musterix.com', '123456');
      if (error) {
        toast({
          title: "Hata",
          description: error.message || "Demo hesabına giriş yapılamadı.",
          variant: "destructive"
        });
      } else {
        navigate(postLoginRoute);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast({
        title: "Hata",
        description: "Lütfen tüm alanları doldurun.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error, user } = await signIn(formData.email, formData.password);

      if (error) {
        toast({
          title: "Giriş Hatası",
          description: error.message || "E-posta veya şifre yanlış. Lütfen tekrar deneyin.",
          variant: "destructive"
        });
      } else if (user) {
        toast({
          title: "Başarılı",
          description: "Giriş başarılı! Ana sayfaya yönlendiriliyorsunuz...",
        });
        
        // Check app version after successful login
        console.log('[Login] Checking app version after login...');
        try {
          const versionResult = await checkAppVersion();
          console.log('[Login] Version check result:', versionResult);
          
          if (versionResult.needsUpdate && versionResult.requirement) {
            console.log('[Login] ⚠️ App version is outdated, showing update modal');
            setVersionCheckResult(versionResult);
            setShowUpdateModal(true);
            // Don't navigate if update is required
            return;
          }
        } catch (versionError) {
          console.error('[Login] Error checking app version:', versionError);
          // Continue with navigation even if version check fails
        }
        
        // Check if we should prompt to save credentials (only on native platforms)
        console.log('════════════════════════════════════════════════════════════');
        console.log('[Login] ✅ Login successful! Checking for credential save prompt...');
        console.log('[Login] Checking platform...');
        const isNative = Capacitor.isNativePlatform();
        console.log('[Login] Is native platform:', isNative);
        console.log('[Login] Platform:', Capacitor.getPlatform());
        console.log('[Login] Email:', formData.email);
        
        if (isNative) {
          console.log('[Login] Native platform detected, checking for existing credentials...');
          
          try {
            const biometric = await loadBiometricFunctions();
            if (biometric) {
              // Check if credentials are already saved
              const hasSaved = await biometric.hasCredentialsSaved();
              console.log('[Login] Has saved credentials:', hasSaved);
              
              if (!hasSaved) {
                // No saved credentials - store them in sessionStorage for Index page to prompt
                console.log('[Login] ⚠️  No saved credentials - storing for Index page prompt');
                
                // Store credentials and flag in sessionStorage
                // Index page will show the dialog
                sessionStorage.setItem('shop-insight-pending-credential-dialog', 'true');
                sessionStorage.setItem('shop-insight-pending-email', formData.email);
                sessionStorage.setItem('shop-insight-pending-password', formData.password);
                
                console.log('[Login] ✅ Stored credentials in sessionStorage for Index page');
                console.log('[Login] Flag:', sessionStorage.getItem('shop-insight-pending-credential-dialog'));
                console.log('[Login] Email stored:', sessionStorage.getItem('shop-insight-pending-email') ? 'YES' : 'NO');
                console.log('[Login] Password stored:', sessionStorage.getItem('shop-insight-pending-password') ? 'YES' : 'NO');
                console.log('════════════════════════════════════════════════════════════');
                
                // Navigate to home - Index page will show the dialog
                navigate(postLoginRoute);
              } else {
                // Update existing credentials silently
                console.log('[Login] ℹ️  Updating existing credentials silently...');
                const updateResult = await biometric.storeCredentials(formData.email, formData.password);
                
                if (updateResult) {
                  console.log('[Login] ✅ Credentials updated successfully');
                } else {
                  console.error('[Login] ❌ Failed to update credentials');
                }
                navigate(postLoginRoute);
              }
            } else {
              console.log('[Login] ⚠️  Biometric functions not available, navigating directly...');
              navigate(postLoginRoute);
            }
          } catch (credError) {
            console.error('[Login] ❌ Error checking/saving credentials:', credError);
            // Navigate anyway if credential check fails
            navigate(postLoginRoute);
          }
        } else {
          // Not on native platform, just navigate
          console.log('[Login] ⚠️  Not on native platform (web) - navigating directly...');
          navigate(postLoginRoute);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Giriş Hatası",
        description: "Bir hata oluştu. Lütfen tekrar deneyin.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Debug log to see if component is rendering with dialog state

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden glass-panel-strong">
        
        {/* Left Side - Branding */}
        <div className="relative p-8 sm:p-12 lg:p-16 flex flex-col justify-center min-h-[300px] lg:min-h-[600px]">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-accent/10 to-transparent"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-accent/20 flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">MüşteriX</h1>
                <p className="text-sm text-muted-foreground">Mağaza Yönetimi Sistemi</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                Mağaza Yönetiminizi
                <span className="block text-accent">Modernleştirin</span>
              </h2>
              
              <p className="text-lg text-foreground/80 leading-relaxed">
                Müşteri takibi, envanter yönetimi ve satış raporları ile işletmenizi büyütün.
              </p>
              
              <div className="space-y-3 pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent"></div>
                  <span className="text-foreground/70">Detayli müşteri takibi</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent"></div>
                  <span className="text-foreground/70">QR kod ile hızlı işlemler</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent"></div>
                  <span className="text-foreground/70">Gerçek zamanlı raporlar</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center bg-white/5">
          <div className="w-full max-w-sm mx-auto">
            <div className="mb-8">
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">Giriş Yap</h3>
              <p className="text-foreground/70">
                Hesabınıza giriş yaparak devam edin
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  E-posta
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="pl-10 glass-panel"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Şifre
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Şifrenizi girin"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="pl-10 pr-10 glass-panel"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="remember"
                  type="checkbox"
                  className="rounded border-white/20"
                  disabled={isLoading}
                />
                <Label htmlFor="remember" className="text-sm text-foreground/70">
                  Beni hatırla
                </Label>
              </div>

              <div className="text-sm text-foreground/60 text-center">
                Şifrenizi unuttuysanız{' '}
                <a 
                  href="mailto:hello@idatech.online" 
                  className="text-accent hover:text-accent/80 transition-colors"
                >
                  hello@idatech.online
                </a>
                {' '}ile iletişime geçiniz.
              </div>

              <Button
                type="submit"
                className="w-full gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Giriş yapılıyor...
                  </>
                ) : (
                  'Giriş Yap'
                )}
              </Button>
            </form>

            {/* Demo Login Button */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={handleDemoLogin}
                disabled={isLoading}
              >
                <Play className="w-4 h-4" />
                Demo Girişi
              </Button>
              <p className="text-xs text-center text-foreground/40 mt-2">
                demo@musterix.com ile örnek hesabı deneyin
              </p>
            </div>

            {/* Signup Link */}
            {!isCarouselOnlyApp && (
              <div className="mt-4 text-center">
                <p className="text-sm text-foreground/60">
                  Hesabınız yok mu?{' '}
                  <button
                    onClick={() => navigate('/signup')}
                    className="text-accent hover:text-accent/80 transition-colors font-medium"
                  >
                    Kayıt Olun
                  </button>
                </p>
              </div>
            )}

            {/* Face ID Login Button - Show only if credentials are stored */}
            {hasStoredCredentials && biometricAvailable && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleBiometricLogin}
                  disabled={isLoading || isBiometricChecking}
                >
                  <Fingerprint className="w-4 h-4" />
                  {isBiometricChecking ? 'Doğrulanıyor...' : 'Face ID ile Giriş'}
                </Button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Update Required Modal */}
      {versionCheckResult && versionCheckResult.requirement && (
        <UpdateRequiredModal
          isOpen={showUpdateModal}
          title={versionCheckResult.requirement.message_title}
          description={versionCheckResult.requirement.message_description}
          appStoreUrl={getAppStoreUrl(versionCheckResult.requirement, versionCheckResult.platform)}
          platform={versionCheckResult.platform}
          currentVersion={versionCheckResult.currentVersion}
          minimumVersion={versionCheckResult.requirement.minimum_version}
        />
      )}
    </div>
  );
};

export default Login;